<template>
  <div>
    <section class="mb-4 d-flex align-center">
      <h3 v-t="'page.account.transactions.title'" class="text-h5 text-uppercase font-weight-extrabold" />
      <v-spacer />
      <v-btn
        color="grey300"
        tile small class="unwidth"
        :ripple="false" :loading="loading"
        @click="fetchApiData"
      >
        <fai :icon="['fas','sync']" class="grey100--text" />
      </v-btn>
    </section>

    <section>
      <v-data-table
        class="rounded-0 data-table"
        :headers="headers"
        :items="entries"
        :options.sync="options"
        :footer-props="{itemsPerPageOptions: [5, 10, 25]}"
        :server-items-length="total"
        :expanded="expanded"
        :loading="loading"
        @pagination="onPagination"
      >
        <template #item.type="{ item, value }">
          <!-- TODO super dirty move to comp -->
          <div class="d-flex align-center font-weight-semibold">
            <span v-if="item.cryptoCode" class="text-uppercase mr-1 d-inline-flex align-center">
              <cryptoicon :symbol="item.cryptoCode" size="1em" class="mr-1 mb-1" />
              {{ item.cryptoCode }}
            </span>
            <span class="grey100--text">{{ item.title }}</span>
          </div>
        </template>
        <template #item.createdAt="{ value }">
          <v-tooltip top color="primary500">
            <template #activator="{on}">
              <span v-on="on">{{ value | relativeTime('twitter') }}</span>
            </template>
            <span>{{ value | toDateFormat('YYYY-MM-DD HH:mm:ss') }}</span>
          </v-tooltip>
        </template>
        <template #item.status="{ item, value }">
          <span v-if="+value === TX_STATES.pending">
            <fai
              :icon="['fad', 'sync']"
              spin class="blue400--text mr-2"
            />
            <span v-t="'common.tx_status.pending'" class="text-capitalize blue300--text" />
          </span>
          <span
            v-else-if="+value === TX_STATES.cancelled"
            v-t="`common.tx_status.${STATES_TX[value]}`"
            class="text-capitalize text--disabled"
          />
          <span
            v-else-if="+value === TX_STATES.failure"
            v-t="`common.tx_status.${STATES_TX[value]}`"
            class="text-capitalize primary400--text"
          />
          <span v-else v-t="`common.tx_status.${STATES_TX[value]}`" class="text-capitalize blue300--text" />
        </template>
        <template #item.amount="{ value, item }">
          <span v-if="!value" class="text--disabled">&mdash;</span>
          <span
            v-else :class="{
              'faded-5': +item.status === TX_STATES.failure || +item.status === TX_STATES.cancelled
            }"
            class="mr-2"
          >
            <scrap v-if="item.isScrap" :amount="Math.abs(value)" />
            <span v-else>${{ Math.abs(value) | toScrap }}</span>
          </span>

          <span v-if="value && +item.status !== TX_STATES.failure && +item.status !== TX_STATES.cancelled">
            <fai
              v-if="item.trend === 'up' && item.type === 'affiliate-kickback'"
              :icon="['fas', 'angle-up']"
              class="blue400--text"
            />
            <fai v-else-if="item.trend === 'up'" :icon="['fas', 'angle-up']" class="green500--text" />
            <fai v-else-if="item.trend === 'down'" :icon="['fas', 'angle-down']" class="primary500--text" />
          </span>
        </template>
        <template #item.actions="{ item }">
          <fai
            :icon="['fad', 'info-square']"
            class="text-body-1 mt-1 blue500--text link"
            @click="onExpandItem(item)"
          />
        </template>

        <!-- transaction info -->
        <template #expanded-item="{ headers: head, item }">
          <transition name="fade" appear>
            <td :colspan="head.length" class="grey800 py-3 transaction-detail-container">
              <TransactionDetail :transaction="item" :itemdb="itemdb" />
            </td>
          </transition>
        </template>
      </v-data-table>
    </section>
  </div>
</template>

<script>
import { CRYPTO_CODES, TX_STATES } from '@/utils/constants'
import { invertObject } from '@/utils'
import { mapGetters } from 'vuex'
import TransactionDetail from './components/TransactionDetail'

const STATES_TX = invertObject(TX_STATES)

const TX_TITLES = {
  withdraw: {
    rust: 'page.account.transactions.type.rust_withdraw',
    crypto: 'page.account.transactions.type.crypto_withdraw',
  },
  deposit: {
    skinsback: 'page.account.transactions.type.skinsback_deposit',
    rust: 'page.account.transactions.type.rust_deposit',
    crypto: 'page.account.transactions.type.crypto_deposit',
    offerwall: 'page.account.transactions.type.offers',
    giftcard: 'page.account.transactions.type.giftcard',
    cash: 'page.account.transactions.type.cash',
  },
  promo: {
    'daily-case': 'page.account.transactions.type.daily_case',
    'level-case': 'page.account.transactions.type.level_case',
    'affiliate-reward': 'page.account.transactions.type.affiliate_reward',
    giftcode: 'page.account.transactions.type.giftcode',
    leaderboard: 'page.account.transactions.type.leaderboard',
    faucet: 'page.account.transactions.type.faucet',
  },
}

const TX_TRENDS = {
  withdraw: 'down',
  buy: 'down',
  support: '', // depends on val
  swap: '', // depends on val
  deposit: 'up',
  sell: 'up',
  faucet: 'up',
}

export default {
  components: { TransactionDetail },
  data() {
    return {
      loading: false,
      total: 0,
      expanded: [],
      options: {
        sortBy: ['createdAt'],
        sortDesc: [true],
        itemsPerPage: 10,
        mustSort: true,
      },
      headers: [
        { text: this.$t('common.type'), value: 'type' },
        {
          text: this.$t('common.status'), value: 'status', sortable: false,
        },
        { text: this.$t('common.amount'), value: 'amount', align: 'end' },
        { text: this.$t('common.time'), value: 'createdAt', align: 'end' },
        { text: '', value: 'actions', width: 8 },
      ],
      entries: [],
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/session',
      itemdb: 'items/items',
    }),
    TX_STATES() {
      return TX_STATES
    },
    STATES_TX() {
      return STATES_TX
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchApiData()
      },
      deep: true,
    },
  },
  methods: {
    async fetchApiData() {
      this.loading = true

      const {
        sortBy, sortDesc, page, itemsPerPage,
      } = this.options

      try {
        // TODO move to store
        const { entries, total } = await this.$socket.request('user.getTransactionHistory', {
          page,
          sortBy: sortBy[0],
          asc: !sortDesc[0],
          limit: itemsPerPage,
        })

        this.entries = entries.map(i => {
          let trend = TX_TRENDS[i.type]

          if (!trend) {
            trend = i.amount > 0 ? 'up' : 'down'
          }

          return {
            ...i,
            title: this.$t(TX_TITLES[i.type]?.[i.method] ?? `page.account.transactions.type.${i.type}`),
            cryptoCode: CRYPTO_CODES[i.data?.currency],
            isScrap: this.isScrapTransaction(i),
            trend,
          }
        })
        this.total = total
      } catch (error) {
        this.entries = []
        this.$toast.error(error.message)
      } finally {
        this.loading = false
      }
    },
    onExpandItem(item) {
      const [expandedItem] = this.expanded

      if (item.id === expandedItem?.id) {
        this.expanded = []
      } else {
        this.expanded = [item]
      }
    },
    onPagination() {
      this.expanded = []
    },
    isScrapTransaction(tx) {
      if (tx.type === 'swap') return true
      if (['withdraw', 'deposit'].includes(tx.type)) {
        if (tx.method === 'rust') return false
      }
      if (tx.method === 'reward') {
        return !tx.items
      }
      if (tx.method === 'daily-case') return false
      return true
    },
  },
}
</script>

<style lang="scss" scoped>
.data-table ::v-deep {
  background-color: grey(600);

  .v-data-table-header {
    background-color: grey(500);
  }

  .v-data-footer {
    background-color: grey(500);
  }

  .v-data-table__expanded__row {
    td {
      border-bottom: 0 !important;
    }
  }

  .transaction-detail-container {

  }
}

.pagination ::v-deep {
  .v-pagination__item,
  .v-pagination__navigation {
    border-radius: 0;
    background-color: grey(500);
    box-shadow: none;
  }
}
</style>
